import { Box, Button, Group } from '@mantine/core'
import { isNotEmpty, useForm } from '@mantine/form'
import { FormattedMessage, useIntl } from 'react-intl'
import { LabelMultiSelect } from '@/components/labels/LabelMultiSelect/LabelMultiSelect'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import {
  Dataset,
  DatasetType,
  DatasetVersion,
  LabelingType
} from '@/types/dataset'
import { MAX_ITEMS_WITHOUT_PAGINATION } from '@/utils/pagination'
import {
  DatasetSelectHandler,
  DatasetSelectionValues
} from '../DatasetSelect/DatasetSelectHandler'

export type SubmitValues = {
  sourceDatasetVersionId: string
  label_ids: string[]
}

type FormValues = {
  sourceDataset: Dataset | null
  sourceDatasetVersion: DatasetVersion | null
  label_ids: string[]
}

type ImageCropHandlerProps = {
  onCancel: () => void
  onSubmit: (values: SubmitValues) => void
}

export const ImageCropForm = ({
  onCancel,
  onSubmit
}: ImageCropHandlerProps) => {
  const intl = useIntl()

  const form = useForm<FormValues>({
    initialValues: {
      sourceDataset: null,
      sourceDatasetVersion: null,
      label_ids: []
    },

    validate: {
      sourceDatasetVersion: isNotEmpty(
        intl.formatMessage({
          id: 'datasets.crops.datasetSelect.required'
        })
      ),
      label_ids: isNotEmpty(
        intl.formatMessage({
          id: 'annotation.classes.required'
        })
      )
    }
  })

  const { sourceDataset: selectedDataset, label_ids: selectedLabelIds } =
    form.getValues()

  const handleSubmit = (values: FormValues) => {
    const { sourceDatasetVersion, label_ids } = values

    if (!sourceDatasetVersion) {
      return
    }

    onSubmit({
      sourceDatasetVersionId: sourceDatasetVersion.id,
      label_ids
    })
  }

  const handleDatasetChange = (
    datasetSelectionValues: DatasetSelectionValues
  ) => {
    const { dataset, version } = datasetSelectionValues
    form.setFieldValue('sourceDataset', dataset)
    form.setFieldValue('sourceDatasetVersion', version)
    form.setFieldValue(
      'label_ids',
      dataset?.labels.map((label) => label.id) || [] // preselect all labels from the dataset
    )
  }

  return (
    <form noValidate onSubmit={form.onSubmit((values) => handleSubmit(values))}>
      <PlatformAlert variant="info" mb="xl" compact>
        <FormattedMessage id="datasets.crops.info" />
      </PlatformAlert>

      <DatasetSelectHandler
        listParams={{
          is_library: false,
          ordering: 'name',
          limit: MAX_ITEMS_WITHOUT_PAGINATION,
          dataset_type: DatasetType.Primary,
          labeling_type: LabelingType.ObjectDetection // only primary datasets with object detection labeling type are allowed for cropping
        }}
        inputProps={{
          label: intl.formatMessage({
            id: 'datasets.crops.datasetSelect.label'
          }),
          error: form.errors.sourceDatasetVersion
        }}
        preselectFirst
        onChange={handleDatasetChange}
      />

      {selectedDataset && (
        <Box mt="xl">
          <LabelMultiSelect
            label={<FormattedMessage id="datasets.crops.classesSelect.label" />}
            values={selectedLabelIds}
            options={selectedDataset.labels.map((label) => ({
              labelId: label.id,
              name: label.name,
              color: label.color
            }))}
            error={form.errors.label_ids}
            onChange={(values) => form.setFieldValue('label_ids', values)}
          />
        </Box>
      )}

      <Group justify="end" mt={200}>
        <Button miw={120} variant="outline" onClick={onCancel}>
          <FormattedMessage id="cancel" />
        </Button>

        <Button type="submit" miw={160}>
          <FormattedMessage id="datasets.crops.create" />
        </Button>
      </Group>
    </form>
  )
}
